/**
 * SB5-B.2 — Contract Position Edit modal.
 *
 * Source: `03-page-contract-position.md` §"Edit Modal".
 *
 * Layout mirrors the existing Site Edit modal — header read-only
 * fields above an editable section (pricing placeholder + externalIds).
 *
 * Read-only header (all immutable per spec):
 *   position_code, contract_position_id (UUID), project_name,
 *   site_name, asset_name (assetDisplayLabel), created_by, created_at,
 *   updated_by, updated_at. `deleted_by` + `deleted_at` only render
 *   when state ∈ {WITHDRAWN, REJECTED}.
 *
 * Editable:
 *   - pricing — read-only placeholder per Q15
 *   - externalIds — repeating {provider, id} editor; PATCH on save
 *
 * State action buttons per Q16:
 *   - CREATED|SCHEDULED → Withdraw (warning) → confirm → POST /withdraw
 *   - EXECUTION → Reject (error) → confirm → POST /reject
 *   - COMPLETED → no action button
 *   - WITHDRAWN|REJECTED → Reactivate → ComingSoonDialog (not implemented yet)
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCanEditResourceFor } from "@aeriosiq/ui";
import { ComingSoonDialog } from "../shared/index.js";
import {
  useContractPositionDetailQuery,
  useRejectContractPositionMutation,
  useUpdateContractPositionMutation,
  useWithdrawContractPositionMutation,
} from "./useContractPositions.js";
import {
  ExternalIdsEditor,
  externalIdsObjectToRows,
  externalIdsRowsEqual,
  externalIdsRowsToObject,
  type ExternalIdRow,
} from "./ExternalIdsEditor.js";
import { PricingPlaceholder } from "./PricingPlaceholder.js";
import type {
  ContractPositionState,
  ContractPositionUserRef,
} from "./types.js";
import { XIcon } from "@aeriosiq/ui/icons";

export interface ContractPositionEditModalProps {
  id: string | null;
  onClose: () => void;
}

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return value;
}

function formatUserRef(ref: ContractPositionUserRef | null): string {
  if (!ref) return "—";
  const first = ref.firstName?.trim() ?? "";
  const last = ref.lastName?.trim() ?? "";
  const tenant = ref.tenantName?.trim() ?? "";
  const name = `${first} ${last}`.trim();
  if (name.length === 0 && tenant.length === 0) return "Unknown";
  if (name.length === 0) return `(${tenant})`;
  if (tenant.length === 0) return name;
  return `${name} (${tenant})`;
}

function ReadOnlyField({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}): ReactElement {
  return (
    <TextField
      label={label}
      value={value}
      size="small"
      fullWidth
      slotProps={{
        htmlInput: {
          readOnly: true,
          "aria-readonly": "true",
          ...(testId ? { "data-testid": testId } : {}),
        },
        inputLabel: { shrink: true },
      }}
    />
  );
}

interface ActionAffordance {
  kind: "withdraw" | "reject" | "reactivate" | "none";
}

function affordanceFor(state: ContractPositionState): ActionAffordance {
  switch (state) {
    case "CREATED":
    case "SCHEDULED":
      return { kind: "withdraw" };
    case "EXECUTION":
      return { kind: "reject" };
    case "WITHDRAWN":
    case "REJECTED":
      return { kind: "reactivate" };
    case "COMPLETED":
      return { kind: "none" };
  }
}

export function ContractPositionEditModal({
  id,
  onClose,
}: ContractPositionEditModalProps): ReactElement {
  const open = id !== null;
  const { data, isPending, isError } = useContractPositionDetailQuery(id);
  // SB6-CTRP-02 — resource-aware role gate. Conditional matrix cell
  // resolves true for tenant_admin / project_manager / platform_admin
  // within their own tenant. Pre-SB6 the modal used the unconditional
  // useCanEditResource which always returned false, so every user
  // (including tenant_admin) saw a read-only modal with no
  // Withdraw / Reject affordances. Christian [00:39:49]: "As tenant
  // admin I must be able to change parameters in my contract position."
  const canEdit = useCanEditResourceFor("project.create");

  const [externalIdsRows, setExternalIdsRows] = useState<ExternalIdRow[]>([]);
  const [originalRows, setOriginalRows] = useState<ExternalIdRow[]>([]);
  const [confirmKind, setConfirmKind] = useState<"withdraw" | "reject" | null>(
    null,
  );
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate the editable section from the detail row when it loads.
  useEffect(() => {
    if (!data) return;
    const rows = externalIdsObjectToRows(data.externalIds);
    setExternalIdsRows(rows);
    setOriginalRows(rows);
    setError(null);
  }, [data]);

  const updateMut = useUpdateContractPositionMutation(id ?? "");
  const withdrawMut = useWithdrawContractPositionMutation(id ?? "");
  const rejectMut = useRejectContractPositionMutation(id ?? "");

  const externalIdsDirty = useMemo(
    () => !externalIdsRowsEqual(externalIdsRows, originalRows),
    [externalIdsRows, originalRows],
  );

  function handleSave(): void {
    if (!id) return;
    setError(null);
    updateMut.mutate(
      { externalIds: externalIdsRowsToObject(externalIdsRows) },
      {
        onSuccess: (next) => {
          // Re-hydrate the "original" baseline so the dirty-state
          // resets without closing the modal.
          const rows = externalIdsObjectToRows(next.externalIds);
          setExternalIdsRows(rows);
          setOriginalRows(rows);
        },
        onError: (e: unknown) => {
          const msg = e instanceof Error ? e.message : "Update failed";
          setError(msg);
        },
      },
    );
  }

  function handleStateChange(kind: "withdraw" | "reject"): void {
    const mut = kind === "withdraw" ? withdrawMut : rejectMut;
    mut.mutate(undefined, {
      onSuccess: () => {
        setConfirmKind(null);
        onClose();
      },
      onError: (e: unknown) => {
        const msg = e instanceof Error ? e.message : `${kind} failed`;
        setError(msg);
        setConfirmKind(null);
      },
    });
  }

  const detail = data;
  const affordance: ActionAffordance | null = detail
    ? affordanceFor(detail.state)
    : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="contract-position-edit-title"
      >
        <DialogTitle id="contract-position-edit-title">
          Edit Contract Position
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <XIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isPending ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isError || !detail ? (
            <Alert severity="error">
              Couldn&apos;t load contract position.
            </Alert>
          ) : (
            <Stack spacing={2}>
              {/* SB6-CTRP-02 — read-only banner mirroring the Asset
                  edit modal pattern. Surfaces when the user's role
                  forbids editing this position. Christian [00:39:49]:
                  "If I'm not allowed to change anything we should show
                  the banner as we do in the other models in the asset
                  management models — 'read only for you' — and then we
                  hide save and cancel buttons." */}
              {!canEdit ? (
                <Alert severity="info" data-testid="cp-read-only-banner">
                  Read-only for your role. Contact a tenant_admin or
                  platform_admin to edit this contract position.
                </Alert>
              ) : null}
              {error ? <Alert severity="error">{error}</Alert> : null}
              <Stack direction="row" spacing={2}>
                <ReadOnlyField
                  label="Position Code"
                  value={String(detail.positionCode)}
                  testId="cp-position-code"
                />
                <ReadOnlyField
                  label="Contract Position ID"
                  value={detail.contractPositionId}
                  testId="cp-id"
                />
              </Stack>
              <ReadOnlyField label="Project" value={detail.projectName} />
              <ReadOnlyField label="Site" value={detail.siteName ?? "—"} />
              <ReadOnlyField
                label="Asset"
                value={detail.assetDisplayLabel}
                testId="cp-asset-display"
              />
              <Divider />
              <PricingPlaceholder />
              <ExternalIdsEditor
                value={externalIdsRows}
                onChange={setExternalIdsRows}
                readOnly={!canEdit}
              />
              <Divider />
              <Stack direction="row" spacing={2}>
                <ReadOnlyField
                  label="Created by"
                  value={formatUserRef(detail.createdBy)}
                />
                <ReadOnlyField
                  label="Created at"
                  value={formatTimestamp(detail.createdAt)}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <ReadOnlyField
                  label="Updated by"
                  value={formatUserRef(detail.updatedBy)}
                />
                <ReadOnlyField
                  label="Updated at"
                  value={formatTimestamp(detail.updatedAt)}
                />
              </Stack>
              {detail.state === "WITHDRAWN" || detail.state === "REJECTED" ? (
                <Stack direction="row" spacing={2}>
                  <ReadOnlyField
                    label="Deleted by"
                    value={formatUserRef(detail.deletedBy)}
                  />
                  <ReadOnlyField
                    label="Deleted at"
                    value={formatTimestamp(detail.deletedAt)}
                  />
                </Stack>
              ) : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          <Box>
            {detail && affordance ? (
              <>
                {affordance.kind === "withdraw" && canEdit ? (
                  <Button
                    color="warning"
                    variant="outlined"
                    aria-label="Withdraw contract position"
                    onClick={() => {
                      setConfirmKind("withdraw");
                    }}
                  >
                    Withdraw
                  </Button>
                ) : null}
                {affordance.kind === "reject" && canEdit ? (
                  <Button
                    color="error"
                    variant="outlined"
                    aria-label="Reject contract position"
                    onClick={() => {
                      setConfirmKind("reject");
                    }}
                  >
                    Reject
                  </Button>
                ) : null}
                {affordance.kind === "reactivate" && canEdit ? (
                  <Button
                    variant="outlined"
                    aria-label="Reactivate contract position"
                    onClick={() => {
                      setReactivateOpen(true);
                    }}
                  >
                    Reactivate
                  </Button>
                ) : null}
              </>
            ) : null}
          </Box>
          {/* SB6-CTRP-02 — when the user lacks edit rights, replace the
              Cancel + Save pair with a single Close button (matches the
              Asset edit modal). Cancel is meaningless when there's
              nothing to cancel. */}
          {canEdit ? (
            <Stack direction="row" spacing={1}>
              <Button onClick={onClose} variant="text">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={!detail || !externalIdsDirty || updateMut.isPending}
                aria-label="Save contract position"
              >
                {updateMut.isPending ? "Saving…" : "Save"}
              </Button>
            </Stack>
          ) : (
            <Button
              onClick={onClose}
              variant="contained"
              aria-label="Close contract position"
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmKind !== null}
        onClose={() => {
          setConfirmKind(null);
        }}
        maxWidth="xs"
        fullWidth
        aria-labelledby="cp-confirm-title"
      >
        <DialogTitle id="cp-confirm-title">
          {confirmKind === "withdraw"
            ? "Withdraw position?"
            : "Reject position?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmKind === "withdraw"
              ? "Withdrawing a contract position is permanent and cannot be undone without re-creation."
              : "Rejecting a contract position is permanent and cannot be undone without re-creation."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmKind(null);
            }}
            variant="text"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmKind) handleStateChange(confirmKind);
            }}
            variant="contained"
            color={confirmKind === "withdraw" ? "warning" : "error"}
            disabled={withdrawMut.isPending || rejectMut.isPending}
          >
            {confirmKind === "withdraw" ? "Withdraw" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <ComingSoonDialog
        open={reactivateOpen}
        onClose={() => {
          setReactivateOpen(false);
        }}
        title="Coming soon"
        body="This routine is not implemented yet."
      />
    </>
  );
}
